// Mathematical utilities for qubit calculations
/**
 * Calculate the Stokes vector from theta, phi, and coherence
 */
export const calculateStokesVector = (
  theta: number,
  phi: number,
  coherence: number,
) => {
  // Calculate probabilities from theta
  const p0 = Math.cos(theta / 2) ** 2
  const p1 = Math.sin(theta / 2) ** 2
  // Calculate z component (preference/valence)
  const z = p1 - p0
  // Calculate x and y components based on coherence
  // x ≈ "openness/potency", y ≈ "activity/action"
  const maxCoherence = Math.sqrt(p0 * p1)
  const c = coherence * maxCoherence
  const x = 2 * c * Math.cos(phi)
  const y = 2 * c * Math.sin(phi)
  return { x, y, z }
}
/**
 * Convert Stokes vector to spherical RGB according to Eq. 18 from Surov's paper
 */
export const calculateSphericalRGB = (x: number, y: number, z: number) => {
  // Implement Eq. 18 from the paper
  const rTilde = (1 / 3) * (x + Math.sqrt(3) * y + z)
  const gTilde = (1 / 3) * (x - Math.sqrt(3) * y + z)
  const bTilde = (1 / 3) * (-2 * x + z)
  return { rTilde, gTilde, bTilde }
}
/**
 * Map spherical RGB to standard RGB (0-1 range) according to Eq. 17
 */
export const calculateRGB = (x: number, y: number, z: number) => {
  const { rTilde, gTilde, bTilde } = calculateSphericalRGB(x, y, z)
  // Implement Eq. 17 from the paper
  // Map from [-1,1] to [0,1] range
  const r = (rTilde + 1) / 2
  const g = (gTilde + 1) / 2
  const b = (bTilde + 1) / 2
  return { r, g, b }
}
/**
 * Calculate the Born rule probabilities from theta
 */
export const calculateProbabilities = (theta: number) => {
  const p0 = Math.cos(theta / 2) ** 2
  const p1 = Math.sin(theta / 2) ** 2
  return { p0, p1 }
}
